'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrdersApi } from '@/lib/Apis';
import { getApiConfiguration } from '@/lib/api-adapter';
import { PopulatedOrder } from '@/lib/Apis/models';
import { useAuth } from '@/providers/AuthProvider';

export function useOrders(
  page: number = 1,
  limit: number = 10,
  orderStatus?: string,
  paymentStatus?: string
) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const resolvedUserId = (user as any)?._id || (user as any)?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orders', resolvedUserId, page, limit, orderStatus, paymentStatus],
    queryFn: async () => {
      const response = await new OrdersApi(getApiConfiguration()).getAllOrders(
        paymentStatus && paymentStatus !== 'All' ? paymentStatus : undefined,
        orderStatus && orderStatus !== 'All' ? orderStatus : undefined,
        page,
        limit,
        undefined,
        undefined,
        undefined,
        resolvedUserId
      );
      return {
        orders: response.data.data || [],
        pagination: {
          page: response.data.page || page,
          limit: response.data.limit || limit,
          total: response.data.total ?? 0,
          totalPages: response.data.totalPages || 1,
        },
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!resolvedUserId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await new OrdersApi(getApiConfiguration()).deleteOrder(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const deleteOrder = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      return true;
    } catch (err) {
      console.error('Failed to delete order:', err);
      return false;
    }
  };

  return {
    orders: data?.orders || [],
    isLoading,
    error: error as Error | null,
    pagination: data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
    refetch,
    deleteOrder,
  };
}

export function useOrder(id: string, guestEmail?: string) {
  const { user, isAuthReady } = useAuth();
  const resolvedUserId = (user as any)?._id || (user as any)?.id;
  const { data, isLoading, error, refetch } = useQuery({
    // Scope cached order details to the authenticated account. Without the
    // user id, switching accounts in one browser could reuse another user's
    // still-fresh order response without making a new API request.
    queryKey: ['order', id, guestEmail ?? null, resolvedUserId ?? null],
    queryFn: async () => {
      const api = new OrdersApi(getApiConfiguration());
      if (guestEmail) {
        // Guest access via public track endpoint — no auth token needed
        const response = await api.trackOrder(id, guestEmail);
        return response.data;
      }
      const response = await api.getSingleOrder(id);
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: !!id && (!!guestEmail || (isAuthReady && !!resolvedUserId)),
  });

  return {
    order: data || null,
    isLoading: (!guestEmail && !isAuthReady) || isLoading,
    error: error as Error | null,
    refetch
  };
}

export function useCurrentOrders() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['currentOrders'],
    queryFn: async () => {
      const response = await new OrdersApi(getApiConfiguration()).getUserOrders();
      return response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    orders: data || [],
    isLoading,
    error: error as Error | null,
    refetch
  };
}
